import {calcUserScore} from 'helpers/common-helper';
import BasePlayer from 'services/sports/default/base-player';
import {getDefaultExposure} from 'helpers/player-helper';
import Const from 'constants';
import Action from 'actions';
import {PlayersPoolBuilder} from 'services/players-pool/players-pool-builder';
import NumberHelper, {toFloat} from 'helpers/number-helper';

export default class Player extends BasePlayer {
    resetSettings({incomingValues: {dispatch}}) {
        const {
            playersData: {players},
            userPointsType,
        } = Action.getState();

        const playersUserPoints = [];
        const captainsUserPoints = [];
        const playersExposure = [];

        players.getAllAsArr().map((player) => {
            playersUserPoints.push({
                playerId: player.playerId,
                userPoints: this.exec('applySympathyToPoints', {
                    player,
                    points: player[userPointsType.getUserPointsType()],
                }),
            });

            captainsUserPoints.push({
                playerId: player.playerId,
                userPoints: this.exec('applySympathyToPoints', {
                    player,
                    points: player.getPropValueOfMode(userPointsType.getUserPointsType(), Const.CAPTAIN_MODE),
                    mode: Const.CAPTAIN_MODE,
                }),
            });

            playersExposure.push({
                playerId: player.playerId,
                exposureMin: getDefaultExposure('min'),
                exposureMax: getDefaultExposure('max'),
            });
        });

        dispatch(Action.do('resetExcludePlayersByRange'));
        dispatch(Action.set('PlayersExposure', playersExposure));
        dispatch(Action.set('UserPoints', playersUserPoints, Const.FLEX_MODE, false));
        dispatch(Action.set('UserPoints', captainsUserPoints, Const.CAPTAIN_MODE, false));

        return true;
    }

    getExpandedEntityParams({incomingValues = {}}) {
        const {modesConfig} = Action.getState();
        const {userPointsType = 'adminPoints'} = incomingValues;
        const expandedPlayer = super.getExpandedEntityParams({incomingValues});
        const userPoints = parseFloat(expandedPlayer[userPointsType]);
        const cptPointCoef = modesConfig.getConfig('CPT').getPointCoefficient();
        const captainUserPoints = userPoints * cptPointCoef;
        const captainAdminPoints = expandedPlayer.adminPoints * cptPointCoef;
        const captainPoints = expandedPlayer.points * cptPointCoef;
        const captainSalary = expandedPlayer.salary * cptPointCoef;
        const captainFantasyPoints = expandedPlayer.fantasyPoints * cptPointCoef;
        const PlayerService = this;

        return {
            ...expandedPlayer,
            starting: NumberHelper.int(expandedPlayer.starting, 0),
            userPoints,
            userScore: calcUserScore(userPoints, expandedPlayer.salary, this.userScoreMultiplier),
            lineupRole: expandedPlayer.positionName,
            captain_salary: captainSalary,
            captain_fantasyPoints: toFloat(captainFantasyPoints, 3),
            captain_exposureMax: expandedPlayer.exposureMax,
            captain_exposureMin: expandedPlayer.exposureMin,
            captain_points: toFloat(captainPoints, 3),
            captain_adminPoints: toFloat(captainAdminPoints, 3),
            captain_userPoints: toFloat(captainUserPoints, 3),
            captain_userScore: calcUserScore(captainUserPoints, captainSalary, this.userScoreMultiplier),
            captain_pointsChangedManually: false,
            overall_exposureMax: getDefaultExposure('max'),
            resetExposure() {
                this.overall_exposureMax = getDefaultExposure('max');
                this.exposureMin = getDefaultExposure('min');
                this.exposureMax = getDefaultExposure('max');
                this.captain_exposureMin = getDefaultExposure('min');
                this.captain_exposureMax = getDefaultExposure('max');
            },
            getPropNameOfMode(prop, incomingMode) {
                let mode = incomingMode || this.mode;
                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getPropNameOfLineupRole(prop) {
                let propName = prop;

                if (this.getIsCaptain()) {
                    propName = this.getCapPropName(prop);
                }

                return propName;
            },
            getPropValueOfLineupRole(prop) {
                let value,
                    propName = this.getPropNameOfLineupRole(prop);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getCapPropName(prop) {
                let mode = this.getIsCaptain() ? Const.CAPTAIN_MODE : Const.DEFAULT_MODE;

                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getCapPropValue(prop) {
                let value,
                    propName = this.getCapPropName(prop);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getIsCaptain() {
                return this.lineupRole === Const.CAPTAIN_MODE;
            },
            setAsCaptain() {
                this.lineupRole = Const.CAPTAIN_MODE;
            },
            getLineupRole() {
                return this.lineupRole;
            },
            isModeUsed(mode) {
                return !(this.isConstructor() && mode === Const.CAPTAIN_MODE);
            },
            isFitForLineup(substitutablePlayer) {
                if (substitutablePlayer.positionInLineup === Const.CAPTAIN_MODE) {
                    return this.isDriver();
                }

                return substitutablePlayer.positionIdInLineup === this.positionName;
            },
            isConstructor() {
                return this.positionName === 'CNSTR';
            },
            isDriver() {
                return this.positionName === 'D';
            },
        };
    }

    definePropNameOfMode({incomingValues: {prop, mode}}) {
        const captainPropsList = [
            'exposureMax',
            'exposureMin',
            'points',
            'adminPoints',
            'userPoints',
            'userScore',
            'salary',
            'fantasyPoints',
            'pointsChangedManually',
        ];
        const overallPropsList = ['exposureMax'];

        if (Const.CAPTAIN_MODE === mode && inArray(prop, captainPropsList)) {
            return 'captain_' + prop;
        } else if (Const.OVERALL_MODE === mode && inArray(prop, overallPropsList)) {
            return 'overall_' + prop;
        }

        return prop;
    }

    preparePoolsOfPlayers({incomingValues: {players, filterPlayersBeforePreparingPools}}) {
        return this.exec('createPoolsOfPlayers', {
            players: Object.values(players),
        });
    }

    createPoolsOfPlayers({incomingValues: {players}}) {
        const {playersLock} = Action.getState();
        let builder = new PlayersPoolBuilder(players);

        builder.addTaskOnScenario('position');
        builder.addTaskOnScenario('playerId');
        builder.addTaskOnScenario('allowedPositions');
        builder.addTaskOnProperty('team');
        builder.addTaskOnProperty('exposureMin', function (player) {
            return (
                !player.isExcludedByPoints(Const.FLEX_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.exposureMin > 0
            );
        });
        builder.addTaskOnProperty('exposureMax', function (player) {
            return (
                !player.isExcludedByPoints(Const.FLEX_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.exposureMax < 100
            );
        });
        builder.addTaskOnProperty('captain_exposureMin', function (player) {
            return (
                !player.isExcludedByPoints(Const.CAPTAIN_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.captain_exposureMin > 0
            );
        });
        builder.addTaskOnProperty('captain_exposureMax', function (player) {
            return (
                !player.isExcludedByPoints(Const.CAPTAIN_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.captain_exposureMax < 100
            );
        });
        builder.addTaskOnProperty('overall_exposureMax', function (player) {
            return !playersLock.isLockedAnywhere(player.getId()) && player.overall_exposureMax < 100;
        });

        return builder.execTasks();
    }

    prepareRandomPoints({incomingValues: {}}) {
        return this.exec('prepareRandomPointsByModes', {});
    }

    preparePlayerForLineup({incomingValues = {}}) {
        return this.exec('preparePlayerForLineup_ShowDown', incomingValues);
    }

    preparePlayerDataForDownloading({incomingValues: {player}}) {
        return this.exec('preparePlayerDataForDownloading_ShowDown', {player});
    }

    resetPoints({incomingValues: {dispatch}}) {
        return this.exec('resetPoints_ShowDown', {dispatch});
    }
}
